import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../api';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PLATFORMS = ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok'];

const PLATFORM_COLORS = {
  instagram: '#E1306C',
  facebook:  '#1877F2',
  linkedin:  '#0A66C2',
  twitter:   '#1DA1F2',
  tiktok:    '#010101',
  buffer:    '#168EEA',
};

const PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook:  'Facebook',
  linkedin:  'LinkedIn',
  twitter:   'Twitter / X',
  tiktok:    'TikTok',
  buffer:    'Buffer',
};

const PLATFORM_SETUP_HINTS = {
  instagram: 'Instagram direct publish requires an image. Copy caption and post via Meta Business Suite. For auto-posting, use Buffer.',
  facebook:  'Requires a Facebook Page access token and Page ID. Create one at developers.facebook.com.',
  linkedin:  'Requires LinkedIn OAuth2 access token and your Organization URN (urn:li:organization:XXXX) or Person URN.',
  twitter:   'Requires Twitter OAuth 2.0 Bearer Token with write scope. Create at developer.twitter.com.',
  tiktok:    'TikTok requires video upload. Script is generated for you to record and upload manually.',
  buffer:    'Buffer publishes to all connected platforms. Enter your Buffer access token and a JSON array of profile IDs.',
};

const DRAFT_STATUS_COLORS = {
  pending:   '#D9852C',
  approved:  '#355E3B',
  rejected:  '#c53030',
  published: '#355E3B',
  failed:    '#c53030',
  scheduled: '#6B8F52',
};

const TOPIC_STATUS_COLORS = {
  queued:      '#8A9187',
  approved:    '#355E3B',
  generating:  '#6B8F52',
  draft_ready: '#D9852C',
  rejected:    '#c53030',
};

const NODE_TYPE_LABELS = {
  brand_voice:    'Brand Voice',
  hooks:          'Hooks',
  repurpose_chain:'Repurpose Chain',
  platform:       'Platform',
  audience:       'Audience',
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function ContentStudioPage() {
  const [activeTab, setActiveTab] = useState('pipeline');

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Content Studio</h1>
          <p>Autonomous content production — topics discovered, drafts generated, published to social platforms</p>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 24 }}>
        {[
          { id: 'pipeline', label: 'Pipeline' },
          { id: 'skillgraph', label: 'Skill Graph' },
          { id: 'social', label: 'Social Accounts' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`filter-tab${activeTab === tab.id ? ' active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pipeline'   && <PipelineTab />}
      {activeTab === 'skillgraph' && <SkillGraphTab />}
      {activeTab === 'social'     && <SocialAccountsTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline Tab
// ─────────────────────────────────────────────────────────────────────────────

function PipelineTab() {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicDesc, setNewTopicDesc] = useState('');
  const [addingTopic, setAddingTopic] = useState(false);
  const [error, setError] = useState(null);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getContentTopics();
      setTopics(data.topics || []);
      if (data.topics?.length > 0 && !selectedTopic) {
        setSelectedTopic(data.topics[0]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, [selectedTopic]);

  useEffect(() => { fetchTopics(); }, []);

  const fetchDrafts = useCallback(async (topicId) => {
    setDraftsLoading(true);
    try {
      const data = await api.getContentDrafts({ topic_id: topicId });
      setDrafts(data.drafts || []);
    } catch {
      setDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTopic) fetchDrafts(selectedTopic.id);
  }, [selectedTopic, fetchDrafts]);

  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
  };

  const handleApproveTopic = async (topic) => {
    setActionLoading((p) => ({ ...p, [topic.id]: 'approving' }));
    try {
      await api.updateContentTopic(topic.id, { status: 'approved' });
      setTopics((p) => p.map((t) => t.id === topic.id ? { ...t, status: 'approved' } : t));
      if (selectedTopic?.id === topic.id) setSelectedTopic((p) => ({ ...p, status: 'approved' }));
    } catch (err) {
      alert(err.message || 'Failed to approve topic');
    } finally {
      setActionLoading((p) => ({ ...p, [topic.id]: null }));
    }
  };

  const handleRejectTopic = async (topic) => {
    setActionLoading((p) => ({ ...p, [topic.id]: 'rejecting' }));
    try {
      await api.updateContentTopic(topic.id, { status: 'rejected' });
      setTopics((p) => p.map((t) => t.id === topic.id ? { ...t, status: 'rejected' } : t));
    } catch (err) {
      alert(err.message || 'Failed to reject topic');
    } finally {
      setActionLoading((p) => ({ ...p, [topic.id]: null }));
    }
  };

  const handleGenerateDrafts = async (topic) => {
    setActionLoading((p) => ({ ...p, [topic.id]: 'generating' }));
    try {
      await api.generateDraftsForTopic(topic.id);
      setTopics((p) => p.map((t) => t.id === topic.id ? { ...t, status: 'generating' } : t));
      // Poll for draft_ready after a few seconds
      setTimeout(async () => {
        await fetchTopics();
        if (selectedTopic?.id === topic.id) await fetchDrafts(topic.id);
      }, 4000);
    } catch (err) {
      alert(err.message || 'Generation failed');
    } finally {
      setActionLoading((p) => ({ ...p, [topic.id]: null }));
    }
  };

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopicTitle.trim()) return;
    setAddingTopic(true);
    try {
      const data = await api.createContentTopic({ title: newTopicTitle.trim(), description: newTopicDesc.trim() });
      setTopics((p) => [data.topic, ...p]);
      setSelectedTopic(data.topic);
      setNewTopicTitle('');
      setNewTopicDesc('');
      setShowAddTopic(false);
    } catch (err) {
      alert(err.message || 'Failed to create topic');
    } finally {
      setAddingTopic(false);
    }
  };

  const handleApproveDraft = async (draft) => {
    setActionLoading((p) => ({ ...p, [draft.id]: 'approving' }));
    try {
      await api.updateContentDraft(draft.id, { status: 'approved' });
      setDrafts((p) => p.map((d) => d.id === draft.id ? { ...d, status: 'approved' } : d));
    } catch (err) {
      alert(err.message || 'Failed to approve draft');
    } finally {
      setActionLoading((p) => ({ ...p, [draft.id]: null }));
    }
  };

  const handleRejectDraft = async (draft) => {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    setActionLoading((p) => ({ ...p, [draft.id]: 'rejecting' }));
    try {
      await api.updateContentDraft(draft.id, { status: 'rejected', rejection_reason: reason });
      setDrafts((p) => p.map((d) => d.id === draft.id ? { ...d, status: 'rejected', rejection_reason: reason } : d));
    } catch (err) {
      alert(err.message || 'Failed to reject draft');
    } finally {
      setActionLoading((p) => ({ ...p, [draft.id]: null }));
    }
  };

  const handlePublishDraft = async (draft) => {
    if (!window.confirm(`Publish this ${PLATFORM_LABELS[draft.platform]} post now?`)) return;
    setActionLoading((p) => ({ ...p, [draft.id]: 'publishing' }));
    try {
      const data = await api.publishContentDraft(draft.id);
      setDrafts((p) => p.map((d) => d.id === draft.id ? { ...d, status: 'published', published_url: data.publishedUrl } : d));
      alert(`Published to ${PLATFORM_LABELS[draft.platform]}!${data.publishedUrl ? `\n${data.publishedUrl}` : ''}`);
    } catch (err) {
      alert(`Publish failed: ${err.message}`);
    } finally {
      setActionLoading((p) => ({ ...p, [draft.id]: null }));
    }
  };

  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '--';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>

      {/* Topics sidebar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Topics</h2>
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '6px 12px' }}
            onClick={() => setShowAddTopic((v) => !v)}
          >
            {showAddTopic ? 'Cancel' : '+ Add'}
          </button>
        </div>

        {showAddTopic && (
          <form className="card" style={{ marginBottom: 12, padding: 16 }} onSubmit={handleAddTopic}>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Topic title</label>
              <input
                value={newTopicTitle}
                onChange={(e) => setNewTopicTitle(e.target.value)}
                placeholder="e.g. Best avocados to buy this week"
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Context (optional)</label>
              <textarea
                style={{ width: '100%', padding: 8, border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13, minHeight: 60 }}
                value={newTopicDesc}
                onChange={(e) => setNewTopicDesc(e.target.value)}
                placeholder="Any extra context for the AI..."
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={addingTopic} style={{ width: '100%', fontSize: 13 }}>
              {addingTopic ? 'Creating...' : 'Create & auto-approve'}
            </button>
          </form>
        )}

        {error && <div className="error-box"><strong>Error:</strong> {error}</div>}

        {loading ? (
          <div className="loading">Loading topics...</div>
        ) : topics.length === 0 ? (
          <div className="empty-state" style={{ padding: '20px 0' }}>
            <p style={{ fontSize: 13 }}>No topics yet. The Content Agent discovers them Mon/Wed/Fri at 7 AM, or add one manually.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {topics.map((topic) => (
              <div
                key={topic.id}
                className="card"
                onClick={() => handleSelectTopic(topic)}
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  border: selectedTopic?.id === topic.id ? '2px solid #355E3B' : '1px solid #f0f0f0',
                  transition: 'border 0.1s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, flex: 1 }}>{topic.title}</span>
                  <span
                    className="agent-badge"
                    style={{ backgroundColor: TOPIC_STATUS_COLORS[topic.status] || '#8A9187', fontSize: 10, whiteSpace: 'nowrap' }}
                  >
                    {topic.status}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                  {topic.source} · {formatDate(topic.created_at)}
                </div>
                {topic.status === 'queued' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="action-btn action-btn-approve"
                      style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={(e) => { e.stopPropagation(); handleApproveTopic(topic); }}
                      disabled={!!actionLoading[topic.id]}
                    >
                      {actionLoading[topic.id] === 'approving' ? '...' : 'Approve'}
                    </button>
                    <button
                      className="action-btn action-btn-reject"
                      style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={(e) => { e.stopPropagation(); handleRejectTopic(topic); }}
                      disabled={!!actionLoading[topic.id]}
                    >
                      Reject
                    </button>
                  </div>
                )}
                {topic.status === 'approved' && (
                  <button
                    className="action-btn action-btn-execute"
                    style={{ fontSize: 11, padding: '3px 10px', width: '100%' }}
                    onClick={(e) => { e.stopPropagation(); handleGenerateDrafts(topic); }}
                    disabled={!!actionLoading[topic.id]}
                  >
                    {actionLoading[topic.id] === 'generating' ? 'Generating...' : 'Generate Drafts Now'}
                  </button>
                )}
                {topic.status === 'generating' && (
                  <div style={{ fontSize: 12, color: '#6B8F52', fontStyle: 'italic' }}>Generating drafts...</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drafts panel */}
      <div>
        {!selectedTopic ? (
          <div className="empty-state">
            <p>Select a topic to view its platform drafts.</p>
          </div>
        ) : (
          <>
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>{selectedTopic.title}</h3>
              {selectedTopic.description && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#666' }}>{selectedTopic.description}</p>
              )}
              <div style={{ fontSize: 12, color: '#999' }}>
                Source: {selectedTopic.source} · Status: {selectedTopic.status} · {formatDate(selectedTopic.created_at)}
              </div>
            </div>

            {draftsLoading ? (
              <div className="loading">Loading drafts...</div>
            ) : drafts.length === 0 ? (
              <div className="empty-state">
                <p>
                  {selectedTopic.status === 'approved'
                    ? 'No drafts yet. Click "Generate Drafts Now" to create platform-native content.'
                    : selectedTopic.status === 'queued'
                    ? 'Approve this topic first, then generate drafts.'
                    : 'No drafts found for this topic.'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {PLATFORMS.map((platform) => {
                  const draft = drafts.find((d) => d.platform === platform);
                  if (!draft) return null;
                  return (
                    <DraftCard
                      key={platform}
                      draft={draft}
                      actionLoading={actionLoading}
                      onApprove={handleApproveDraft}
                      onReject={handleRejectDraft}
                      onPublish={handlePublishDraft}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DraftCard({ draft, actionLoading, onApprove, onReject, onPublish }) {
  const [expanded, setExpanded] = useState(false);
  const isLoading = !!actionLoading[draft.id];
  const color = PLATFORM_COLORS[draft.platform] || '#8A9187';

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
              backgroundColor: color, flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 700 }}>{PLATFORM_LABELS[draft.platform]}</span>
          {draft.published_url && (
            <a
              href={draft.published_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#355E3B', textDecoration: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              ↗ View
            </a>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            className="agent-badge"
            style={{ backgroundColor: DRAFT_STATUS_COLORS[draft.status] || '#8A9187', fontSize: 11 }}
          >
            {draft.status}
          </span>
          <span style={{ fontSize: 12, color: '#aaa' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px' }}>
          <pre
            style={{
              whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14,
              lineHeight: 1.6, margin: '0 0 16px', color: '#1a1a1a',
              background: '#fafafa', padding: 14, borderRadius: 8, border: '1px solid #f0f0f0',
            }}
          >
            {draft.draft}
          </pre>

          {draft.rejection_reason && (
            <p style={{ fontSize: 13, color: '#c53030', marginBottom: 12 }}>
              Rejected: {draft.rejection_reason}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(draft.status === 'pending' || draft.status === 'rejected') && (
              <button
                className="action-btn action-btn-approve"
                onClick={() => onApprove(draft)}
                disabled={isLoading}
              >
                {actionLoading[draft.id] === 'approving' ? 'Approving...' : 'Approve'}
              </button>
            )}
            {draft.status === 'pending' && (
              <button
                className="action-btn action-btn-reject"
                onClick={() => onReject(draft)}
                disabled={isLoading}
              >
                Reject
              </button>
            )}
            {draft.status === 'approved' && (
              <button
                className="action-btn action-btn-execute"
                onClick={() => onPublish(draft)}
                disabled={isLoading}
                style={{ backgroundColor: color, color: '#fff', border: 'none' }}
              >
                {actionLoading[draft.id] === 'publishing' ? 'Publishing...' : `Publish to ${PLATFORM_LABELS[draft.platform]}`}
              </button>
            )}
            {draft.status === 'published' && (
              <span style={{ fontSize: 13, color: '#355E3B', fontWeight: 600 }}>
                ✓ Published {draft.published_at ? new Date(draft.published_at).toLocaleDateString('en-ZA') : ''}
              </span>
            )}
            {draft.status === 'failed' && (
              <button
                className="action-btn action-btn-execute"
                onClick={() => onPublish(draft)}
                disabled={isLoading}
              >
                Retry Publish
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Skill Graph Tab
// ─────────────────────────────────────────────────────────────────────────────

function SkillGraphTab() {
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getSkillGraphNodes()
      .then((data) => {
        const sorted = (data.nodes || []).sort((a, b) =>
          (a.node_type + a.name).localeCompare(b.node_type + b.name),
        );
        setNodes(sorted);
        if (sorted.length > 0) {
          setSelectedNode(sorted[0]);
          setEditContent(sorted[0].content);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSelectNode = (node) => {
    setSelectedNode(node);
    setEditContent(node.content);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedNode) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.updateSkillGraphNode(selectedNode.id, { content: editContent });
      setNodes((p) => p.map((n) => n.id === selectedNode.id ? { ...n, content: editContent, version: (n.version || 1) + 1 } : n));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  const groupedNodes = nodes.reduce((acc, node) => {
    const type = node.node_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(node);
    return acc;
  }, {});

  if (loading) return <div className="loading">Loading skill graph...</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start' }}>
      {/* Node list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {Object.entries(groupedNodes).map(([type, typeNodes]) => (
          <div key={type}>
            <div
              style={{
                padding: '8px 14px',
                fontSize: 10,
                fontWeight: 700,
                color: '#aaa',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
              }}
            >
              {NODE_TYPE_LABELS[type] || type}
            </div>
            {typeNodes.map((node) => (
              <div
                key={node.id}
                onClick={() => handleSelectNode(node)}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f8f8f8',
                  backgroundColor: selectedNode?.id === node.id ? '#E8F5E9' : 'transparent',
                  borderLeft: selectedNode?.id === node.id ? '3px solid #355E3B' : '3px solid transparent',
                  fontSize: 13,
                  fontWeight: selectedNode?.id === node.id ? 600 : 400,
                  color: '#1a1a1a',
                }}
              >
                {node.name}
                <span style={{ fontSize: 11, color: '#aaa', marginLeft: 6 }}>v{node.version || 1}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Editor */}
      {selectedNode ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{selectedNode.name}</h3>
              <span style={{ fontSize: 12, color: '#aaa' }}>{NODE_TYPE_LABELS[selectedNode.node_type]} · v{selectedNode.version || 1}</span>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || editContent === selectedNode.content}
            >
              {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Changes'}
            </button>
          </div>
          <textarea
            style={{
              width: '100%',
              minHeight: 500,
              padding: 16,
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.6,
              resize: 'vertical',
              boxSizing: 'border-box',
              color: '#1a1a1a',
            }}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
          />
          <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
            Changes take effect on the next Content Agent run (Mon/Wed/Fri 7 AM UTC).
          </p>
        </div>
      ) : (
        <div className="empty-state"><p>Select a node to edit.</p></div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Social Accounts Tab
// ─────────────────────────────────────────────────────────────────────────────

const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok', 'buffer'];

function SocialAccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [forms, setForms] = useState({});

  useEffect(() => {
    api.getSocialAccounts()
      .then((data) => {
        const existingByPlatform = {};
        for (const a of data.accounts || []) existingByPlatform[a.platform] = a;

        const initial = {};
        for (const platform of SOCIAL_PLATFORMS) {
          const existing = existingByPlatform[platform];
          initial[platform] = {
            account_name:  existing?.account_name || '',
            access_token:  '',  // never pre-fill token (masked on server)
            page_id:       existing?.page_id || '',
            is_active:     existing?.is_active !== 0,
          };
        }
        setForms(initial);
        setAccounts(data.accounts || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (platform, field, value) => {
    setForms((p) => ({ ...p, [platform]: { ...p[platform], [field]: value } }));
  };

  const handleSave = async (platform) => {
    setSaving((p) => ({ ...p, [platform]: true }));
    try {
      await api.upsertSocialAccount(platform, forms[platform]);
      // Refresh accounts list
      const data = await api.getSocialAccounts();
      setAccounts(data.accounts || []);
      alert(`${PLATFORM_LABELS[platform]} credentials saved.`);
    } catch (err) {
      alert(err.message || 'Failed to save');
    } finally {
      setSaving((p) => ({ ...p, [platform]: false }));
    }
  };

  const existingByPlatform = accounts.reduce((acc, a) => { acc[a.platform] = a; return acc; }, {});

  if (loading) return <div className="loading">Loading social accounts...</div>;

  return (
    <div>
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px', backgroundColor: '#E8F5E9', border: '1px solid #C8E6C9' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#2E7D32' }}>How publishing works</h3>
        <p style={{ margin: 0, fontSize: 14, color: '#1B5E20', lineHeight: 1.6 }}>
          Approve drafts in the Pipeline tab, then click "Publish" to push directly to that platform.
          <strong> Facebook and LinkedIn</strong> publish text posts directly.
          <strong> Twitter/X</strong> publishes via API (OAuth Bearer Token required).
          <strong> Instagram and TikTok</strong> generate the copy for manual posting — or connect <strong>Buffer</strong> to handle them automatically.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {SOCIAL_PLATFORMS.map((platform) => {
          const existing = existingByPlatform[platform];
          const form = forms[platform] || {};
          const color = PLATFORM_COLORS[platform] || '#8A9187';
          const isManualOnly = platform === 'instagram' || platform === 'tiktok';

          return (
            <div className="card" key={platform} style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{PLATFORM_LABELS[platform]}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {existing && (
                    <span className="agent-badge" style={{ backgroundColor: existing.is_active ? '#355E3B' : '#c53030', fontSize: 11 }}>
                      {existing.is_active ? 'Connected' : 'Disabled'}
                    </span>
                  )}
                  {isManualOnly && (
                    <span className="agent-badge" style={{ backgroundColor: '#8A9187', fontSize: 11 }}>Manual only</span>
                  )}
                </div>
              </div>

              <div style={{ padding: 16 }}>
                <p style={{ fontSize: 12, color: '#888', margin: '0 0 12px', lineHeight: 1.5 }}>
                  {PLATFORM_SETUP_HINTS[platform]}
                </p>

                {!isManualOnly && (
                  <>
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12 }}>Account name</label>
                      <input
                        style={{ fontSize: 13 }}
                        value={form.account_name || ''}
                        onChange={(e) => handleChange(platform, 'account_name', e.target.value)}
                        placeholder="@handle or display name"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12 }}>
                        Access token
                        {existing?.access_token && <span style={{ color: '#aaa', fontWeight: 400, marginLeft: 6 }}>(leave blank to keep existing)</span>}
                      </label>
                      <input
                        type="password"
                        style={{ fontSize: 13 }}
                        value={form.access_token || ''}
                        onChange={(e) => handleChange(platform, 'access_token', e.target.value)}
                        placeholder={existing?.access_token ? '••••••••' : 'Paste token here'}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12 }}>
                        {platform === 'linkedin' ? 'Author URN (urn:li:organization:XXXXX)' :
                         platform === 'facebook'  ? 'Facebook Page ID' :
                         platform === 'buffer'    ? 'Buffer profile IDs (JSON array, e.g. ["abc","def"])' :
                         'Account / Page ID'}
                      </label>
                      <input
                        style={{ fontSize: 13 }}
                        value={form.page_id || ''}
                        onChange={(e) => handleChange(platform, 'page_id', e.target.value)}
                        placeholder={
                          platform === 'linkedin' ? 'urn:li:organization:1234567' :
                          platform === 'buffer'   ? '["profile_id_1", "profile_id_2"]' :
                          'Page or account ID'
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <input
                        type="checkbox"
                        id={`active-${platform}`}
                        checked={form.is_active !== false}
                        onChange={(e) => handleChange(platform, 'is_active', e.target.checked)}
                        style={{ width: 14, height: 14, cursor: 'pointer' }}
                      />
                      <label htmlFor={`active-${platform}`} style={{ fontSize: 13, cursor: 'pointer' }}>
                        Enabled for publishing
                      </label>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%', fontSize: 13, backgroundColor: color, borderColor: color }}
                      onClick={() => handleSave(platform)}
                      disabled={!!saving[platform]}
                    >
                      {saving[platform] ? 'Saving...' : 'Save Credentials'}
                    </button>
                    {existing?.last_published_at && (
                      <p style={{ fontSize: 12, color: '#aaa', marginTop: 8, marginBottom: 0 }}>
                        Last published: {new Date(existing.last_published_at).toLocaleDateString('en-ZA')}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
